import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, updateDoc, arrayUnion, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { UserManagementUser, User } from '../types';

interface AddUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId?: string;
  programId?: string;
  groupId?: string;
  entityType: 'organization' | 'program' | 'group';
  entityName: string;
  user?: User | null;
}

export function AddUserModal({
  isOpen,
  onClose,
  organizationId,
  programId,
  groupId,
  entityType,
  entityName,
  user
}: AddUserModalProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [showMode, setShowMode] = useState<'choice' | 'assign'>('choice');
  const [searchTerm, setSearchTerm] = useState('');
  const [allUsers, setAllUsers] = useState<UserManagementUser[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserManagementUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [userRole, setUserRole] = useState<'administrator' | 'member' | 'instructor' | 'social_worker' | 'team_manager' | 'program_manager' | 'department_manager'>('member');

  // Load entities to check for managers
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  const [programs, setPrograms] = useState<any[]>([]);

  // Load all users and entities
  useEffect(() => {
    if (isOpen && showMode === 'assign') {
      loadUsers();
      loadEntities();
    }
  }, [isOpen, showMode]);

  // Load entities to check for managers
  const loadEntities = async () => {
    try {
      // Load organizations
      const orgsSnapshot = await getDocs(collection(db, 'organizations'));
      const orgsData = orgsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setOrganizations(orgsData);

      // Load groups
      const groupsSnapshot = await getDocs(collection(db, 'groups'));
      const groupsData = groupsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setGroups(groupsData);

      // Load programs
      const programsSnapshot = await getDocs(collection(db, 'programs'));
      const programsData = programsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPrograms(programsData);
    } catch (error) {
      console.error('Error loading entities:', error);
    }
  };

  // Check if user is a manager in any entity (excluding current entity)
  const isUserManagerInOtherEntities = (userId: string): boolean => {
    // Check if user is an administrator in any organization (except current one)
    const isOrgAdmin = organizations.some(org => 
      org.id !== organizationId && 
      org.administrators && 
      org.administrators.includes(userId)
    );

    // Check if user is a manager in any group (except current one)
    const isGroupManager = groups.some(group => 
      group.id !== groupId && 
      group.managers && 
      group.managers.includes(userId)
    );

    // Check if user is a manager in any program (except current one)
    const isProgramManager = programs.some(program => 
      program.id !== programId && 
      program.managers && 
      program.managers.includes(userId)
    );

    return isOrgAdmin || isGroupManager || isProgramManager;
  };

  // Filter users based on search term
  useEffect(() => {
    if (searchTerm.length < 2) {
      setFilteredUsers([]);
      return;
    }

    const filtered = allUsers.filter(user => {
      // Exclude super_admin users - they can only be added from Users page
      if (user.role === 'super_admin' || user.legacyRole === 'super_admin') {
        return false;
      }
      
      // Exclude users who are managers in other entities
      // (but allow them if they're managers in the current entity)
      if (isUserManagerInOtherEntities(user.id)) {
        return false;
      }
      
      return user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
             user.email.toLowerCase().includes(searchTerm.toLowerCase());
    });
    setFilteredUsers(filtered);
  }, [searchTerm, allUsers, organizations, groups, programs, organizationId, groupId, programId]);

  const loadUsers = async () => {
    setIsLoading(true);
    try {
      const usersRef = collection(db, 'users');
      const snapshot = await getDocs(usersRef);
      const users: UserManagementUser[] = [];
      
      snapshot.forEach((doc) => {
        const userData = doc.data();
        // Exclude deleted users and super_admin users (they can only be added from Users page)
        if (!userData.deleted && userData.role !== 'super_admin' && userData.legacyRole !== 'super_admin') {
          users.push({
            id: doc.id,
            name: userData.name || '',
            email: userData.email || '',
            role: userData.role || '',
            createdAt: userData.createdAt?.toDate?.()?.toISOString() || '',
            lastLoginAt: userData.lastLoginAt?.toDate?.()?.toISOString() || '',
            loginCount: userData.loginCount || 0,
            blocked: userData.blocked || false
          });
        }
      });
      
      setAllUsers(users);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateNewUser = () => {
    const params = new URLSearchParams();
    if (organizationId) params.append('organizationId', organizationId);
    if (programId) params.append('programId', programId);
    if (groupId) params.append('groupId', groupId);
    navigate(`/create?${params.toString()}`);
    onClose();
  };

  const handleAssignUser = async (userId: string) => {
    setIsAssigning(true);
    try {
      // Get user data to get the name
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      const userData = userDoc.data();
      
      // Check if user is super_admin - prevent assignment
      if (userData?.role === 'super_admin' || userData?.legacyRole === 'super_admin') {
        alert(t('addUserModal.superAdminCannotBeAssigned') || 'Super Admin users cannot be assigned to organizations, programs, or groups. Please change the role to user first.');
        setIsAssigning(false);
        return;
      }
      
      const userName = userData?.name || '';

      // Update the user document with the organization/program/group assignment
      // For groups, we'll handle the update later with role assignment
      if (entityType !== 'group') {
      const userUpdateData: any = {};

      if (entityType === 'organization' && organizationId) {
        userUpdateData.organizationId = organizationId;
      } else if (entityType === 'program' && programId) {
        userUpdateData.programId = programId;
        if (organizationId) userUpdateData.organizationId = organizationId;
      }

      await updateDoc(userRef, userUpdateData);
      }

      // Also update the organization/program/group document to add the user
      if (entityType === 'organization' && organizationId) {
        const orgRef = doc(db, 'organizations', organizationId);
        const orgDoc = await getDoc(orgRef);
        if (orgDoc.exists()) {
          const orgData = orgDoc.data();
          
          // Only add to administrators if role is 'administrator'
          if (userRole === 'administrator') {
            const currentAdministrators = orgData.administrators || [];
            const currentAdministratorNames = orgData.administratorNames || [];
            
            // Only add if not already in the list
            if (!currentAdministrators.includes(userId)) {
              await updateDoc(orgRef, {
                administrators: [...currentAdministrators, userId],
                administratorNames: [...currentAdministratorNames, userName]
              });
            }
          }
          // If role is 'member', just setting organizationId is enough
          // The user will appear in the organization's member list via their organizationId
        }
      } else if (entityType === 'program' && programId) {
        const programRef = doc(db, 'programs', programId);
        const programDoc = await getDoc(programRef);
        if (programDoc.exists()) {
          const programData = programDoc.data();
          
          // Update user's programId and organizationRole
          const userRef = doc(db, 'users', userId);
          const userUpdate: any = {
            programId: programId
          };
          
          // Update organizationRole based on selected role for programs
          if (userRole === 'department_manager' || userRole === 'program_manager' || userRole === 'team_manager' || userRole === 'instructor' || userRole === 'social_worker') {
            userUpdate.organizationRole = userRole;
          }
          // For 'member', don't update organizationRole - keep existing or leave as is
          
          await updateDoc(userRef, userUpdate);
          
          // Add to managers if role is 'manager', 'program_manager', or 'team_manager'
          if (userRole === 'department_manager' || userRole === 'program_manager' || userRole === 'team_manager') {
          const currentManagers = programData.managers || [];
          const currentManagerNames = programData.managerNames || [];
          
          if (!currentManagers.includes(userId)) {
            await updateDoc(programRef, {
              managers: [...currentManagers, userId],
              managerNames: [...currentManagerNames, userName]
            });
          }
          }
          // If role is 'member', just setting programId is enough
          // The user will appear in the program's member list via their programId
        }
      } else if (entityType === 'group' && groupId) {
        const groupRef = doc(db, 'groups', groupId);
        const groupDoc = await getDoc(groupRef);
        if (groupDoc.exists()) {
          const groupData = groupDoc.data();
          
          // Update user's groupId, programId, organizationId, and organizationRole
          const userRef = doc(db, 'users', userId);
          const userUpdate: any = {
            groupId: groupId
          };
          
          // Also update programId and organizationId if available
          if (programId) {
            userUpdate.programId = programId;
          }
          if (organizationId) {
            userUpdate.organizationId = organizationId;
          }
          
          // Update organizationRole based on selected role for groups
          // Always update organizationRole when assigning to group (except for 'member')
          if (userRole === 'department_manager' || userRole === 'program_manager' || userRole === 'team_manager' || userRole === 'instructor' || userRole === 'social_worker') {
            userUpdate.organizationRole = userRole;
          }
          // For 'member', don't update organizationRole - keep existing or leave as is
          
          // Always update the user document with groupId and role (if applicable)
          console.log('Updating user with role assignment:', { userId, userRole, userUpdate });
          await updateDoc(userRef, userUpdate);
          
          // Add to managers if role is manager type
          if (userRole === 'department_manager' || userRole === 'program_manager' || userRole === 'team_manager') {
            const currentManagers = groupData.managers || [];
            const currentManagerNames = groupData.managerNames || [];
            
            if (!currentManagers.includes(userId)) {
              await updateDoc(groupRef, {
                managers: [...currentManagers, userId],
                managerNames: [...currentManagerNames, userName]
              });
            }
          }
        }
      }
      
      alert(t('addUserModal.assignSuccess') || 'User assigned successfully!');
      onClose();
    } catch (error) {
      alert(t('addUserModal.assignError') || 'Failed to assign user');
    } finally {
      setIsAssigning(false);
    }
  };

  const handleClose = () => {
    setShowMode('choice');
    setSearchTerm('');
    setFilteredUsers([]);
    setUserRole('member'); // Reset to default
    onClose();
  };

  // Reset role when entity type changes
  useEffect(() => {
    setUserRole('member');
  }, [entityType]);

  if (!isOpen) return null;

  const isRTL = i18n.language === 'he';
  
  // Check if current user is team_manager and entityType is group - hide buttons for them
  // But allow super_admin to always add users
  const isSuperAdmin = user?.role === 'super_admin' || user?.legacyRole === 'super_admin';
  const isTeamManager = user?.organizationRole === 'team_manager';
  const hideButtonsForTeamManager = isTeamManager && entityType === 'group' && !isSuperAdmin;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000,
      direction: isRTL ? 'rtl' : 'ltr'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '30px',
        width: '90%',
        maxWidth: '600px',
        maxHeight: '80vh',
        overflow: 'auto',
        position: 'relative'
      }}>
        {/* Close X Button */}
        <button
          onClick={handleClose}
          style={{
            position: 'absolute',
            top: '15px',
            ...(isRTL ? { left: '15px' } : { right: '15px' }),
            background: 'none',
            border: 'none',
            fontSize: '28px',
            color: '#666',
            cursor: 'pointer',
            width: '30px',
            height: '30px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            transition: 'all 0.2s ease',
            padding: 0,
            lineHeight: 1
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f0f0f0';
            e.currentTarget.style.color = '#000';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = '#666';
          }}
        >
          ×
        </button>

        {showMode === 'choice' ? (
          <>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '24px', color: '#333' }}>
              {t('addUserModal.title')}
            </h3>
            <p style={{ margin: '0 0 30px 0', color: '#666', fontSize: '14px' }}>
              {t('addUserModal.subtitle')}: <strong>{entityName}</strong>
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Create New User Option */}
              {!hideButtonsForTeamManager && (
              <button
                onClick={handleCreateNewUser}
                style={{
                  padding: '20px',
                  border: '2px solid #007acc',
                  borderRadius: '12px',
                  background: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  textAlign: isRTL ? 'right' : 'left'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f0f8ff';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,122,204,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'white';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: '#007acc',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px',
                    color: 'white'
                  }}>
                    ➕
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '18px', fontWeight: '600', color: '#333', marginBottom: '4px' }}>
                      {t('addUserModal.createNew')}
                    </div>
                    <div style={{ fontSize: '13px', color: '#666' }}>
                      {t('addUserModal.createNewDesc')}
                    </div>
                  </div>
                </div>
              </button>
              )}

              {/* Assign Existing User Option */}
              {!hideButtonsForTeamManager && (
              <button
                onClick={() => setShowMode('assign')}
                style={{
                  padding: '20px',
                  border: '2px solid #28a745',
                  borderRadius: '12px',
                  background: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  textAlign: isRTL ? 'right' : 'left'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f0fff4';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(40,167,69,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'white';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: '#28a745',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px',
                    color: 'white'
                  }}>
                    🔗
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '18px', fontWeight: '600', color: '#333', marginBottom: '4px' }}>
                      {t('addUserModal.assignExisting')}
                    </div>
                    <div style={{ fontSize: '13px', color: '#666' }}>
                      {t('addUserModal.assignExistingDesc')}
                    </div>
                  </div>
                </div>
              </button>
              )}
              
              {hideButtonsForTeamManager && (
                <div style={{ 
                  padding: '20px', 
                  textAlign: 'center', 
                  color: '#666',
                  fontSize: '14px',
                  background: '#f8f9fa',
                  borderRadius: '12px',
                  border: '1px solid #e0e0e0'
                }}>
                  {t('addUserModal.noPermission') || 'You do not have permission to add users to groups.'}
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <button
              onClick={() => setShowMode('choice')}
              style={{
                background: 'none',
                border: 'none',
                color: '#007acc',
                fontSize: '14px',
                cursor: 'pointer',
                marginBottom: '16px',
                padding: '4px 8px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              {isRTL ? '→' : '←'} {t('common.back')}
            </button>

            <h3 style={{ margin: '0 0 16px 0', fontSize: '24px', color: '#333' }}>
              {t('addUserModal.searchUsers')}
            </h3>

            {/* Role Selector */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                  {t('addUserModal.userRole') || 'User Role'}:
                </label>
                <select
                  value={userRole}
                  onChange={(e) => setUserRole(e.target.value as any)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  {entityType === 'organization' ? (
                    <>
                  <option value="member">{t('addUserModal.regularMember') || 'Regular Member'}</option>
                  <option value="administrator">{t('addUserModal.administrator') || 'Administrator'}</option>
                    </>
                  ) : entityType === 'program' ? (
                    // For programs, show all organization roles including program_manager
                    <>
                      <option value="member">{t('addUserModal.regularMember') || 'Regular Member'}</option>
                      <option value="department_manager">{t('users.roles.department_manager') || 'Department Manager'}</option>
                      <option value="program_manager">{t('users.roles.program_manager') || 'Program Manager'}</option>
                      <option value="team_manager">{t('users.roles.team_manager') || 'Team Manager'}</option>
                      <option value="instructor">{t('users.roles.instructor') || 'Instructor'}</option>
                      <option value="social_worker">{t('users.roles.social_worker') || 'Social Worker'}</option>
                    </>
                  ) : (
                    // For groups, show all organization roles
                    <>
                      <option value="member">{t('addUserModal.regularMember') || 'Regular Member'}</option>
                      <option value="department_manager">{t('users.roles.department_manager') || 'Department Manager'}</option>
                      <option value="program_manager">{t('users.roles.program_manager') || 'Program Manager'}</option>
                      <option value="team_manager">{t('users.roles.team_manager') || 'Team Manager'}</option>
                      <option value="instructor">{t('users.roles.instructor') || 'Instructor'}</option>
                      <option value="social_worker">{t('users.roles.social_worker') || 'Social Worker'}</option>
                    </>
                )}
                </select>
              </div>

            {/* Search Input */}
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('addUserModal.searchPlaceholder')}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #ddd',
                borderRadius: '8px',
                fontSize: '14px',
                marginBottom: '16px'
              }}
            />

            {/* Loading State */}
            {isLoading && (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                {t('common.loading')}...
              </div>
            )}

            {/* Search Results */}
            {!isLoading && searchTerm.length >= 2 && (
              <>
                {filteredUsers.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                    {t('addUserModal.noResults')}
                  </div>
                ) : (
                  <div style={{
                    maxHeight: '400px',
                    overflowY: 'auto',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px'
                  }}>
                    {filteredUsers.map((user) => (
                      <div
                        key={user.id}
                        style={{
                          padding: '16px',
                          borderBottom: '1px solid #f0f0f0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '12px',
                          flexDirection: isRTL ? 'row-reverse' : 'row'
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '600', fontSize: '15px', color: '#333', marginBottom: '4px' }}>
                            {user.name}
                          </div>
                          <div style={{ fontSize: '13px', color: '#666' }}>
                            {user.email}
                          </div>
                        </div>
                        <button
                          onClick={() => handleAssignUser(user.id)}
                          disabled={isAssigning}
                          style={{
                            padding: '8px 16px',
                            background: '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: isAssigning ? 'not-allowed' : 'pointer',
                            fontSize: '14px',
                            fontWeight: '500',
                            opacity: isAssigning ? 0.6 : 1
                          }}
                        >
                          {t('addUserModal.assign')}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {searchTerm.length < 2 && !isLoading && (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999', fontSize: '14px' }}>
                {t('addUserModal.searchHint')}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

